import { useRef, useState } from 'react';
import { ChevronDown, ChevronRight, ChevronUp, Info, User, Users } from 'lucide-react';
import { WorkflowStageStatusEnum, type SearchWorkflowStageTaskViewModel } from '@generated';
import { useTruncatedElement } from '~/shared/hooks/useTruncatedElement';
import { DialogTrigger } from 'react-aria-components';
import { Popover } from '~/shared/components/Popover';
import { useWorkflowTranslations } from '~/features/workflowing/workflows/useWorkflowTranslations';
import { useGetCategoryLabel } from '~/libraries/categories/useGetCategoryLabel';
import { PressableButton } from '~/shared/PressableButton';
import { useNavigate } from 'react-router-dom';
import { Button } from '~/shared/components/buttons/Button';
import { Badge } from '~/shared/components/Badge';
import type { StatusIntents } from '~/theme/variants';

function isOverdue(dueDate: Date): boolean {
	const now = new Date();
	return dueDate < now;
}

function getStatusIntent(task: SearchWorkflowStageTaskViewModel, isOverdueFlag: boolean): StatusIntents {
	switch (task.workflowStageTaskStatusId) {
		case WorkflowStageStatusEnum.Completed:
			return 'success';
		case WorkflowStageStatusEnum.InProgress:
			return isOverdueFlag ? 'destructive' : 'info';
		case WorkflowStageStatusEnum.Queued:
			return 'neutral';
		case WorkflowStageStatusEnum.Cancelled:
			return 'neutral';
		default:
			return 'neutral';
	}
}

function getCategoryColor(task: SearchWorkflowStageTaskViewModel): string {
	switch (task.category) {
		case 'demographics':
			return 'bg-purple-100 text-purple-700';
		case 'medications':
			return 'bg-orange-100 text-orange-700';
		case 'scheduling':
			return 'bg-cyan-100 text-cyan-700';
		case 'orders':
			return 'bg-indigo-100 text-indigo-700';
		case 'billing':
			return 'bg-emerald-100 text-emerald-700';
		default:
			return 'bg-gray-100 text-gray-700';
	}
}

function formatDate(date: Date, isOverdueFlag: boolean) {
	const now = new Date();
	const diffTime = date.getTime() - now.getTime();
	const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

	const formattedDate = date.toLocaleDateString('en-US', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
		hour: 'numeric',
		minute: '2-digit',
		hour12: true
	});

	if (isOverdueFlag) {
		return `Overdue: ${formattedDate}`;
	}

	if (diffDays === 0) {
		return `Due: Today at ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`;
	} else if (diffDays === 1) {
		return `Due: Tomorrow at ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`;
	} else if (diffDays > 0 && diffDays <= 7) {
		return `Due: ${date.toLocaleDateString('en-US', { weekday: 'long', hour: 'numeric', minute: '2-digit', hour12: true })}`;
	}

	return `Due: ${formattedDate}`;
}

function formatCompletedDate(date: Date) {
	return date.toLocaleDateString('en-US', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
		hour: 'numeric',
		minute: '2-digit',
		hour12: true
	});
}

export function WorkflowStageTaskCard({ task }: { task: SearchWorkflowStageTaskViewModel }) {
	const getCategoryLabel = useGetCategoryLabel();
	const { getTitle } = useWorkflowTranslations();
	const navigate = useNavigate();
	const [isNavigating, setIsNavigating] = useState(false);
	const descriptionRef = useRef<HTMLParagraphElement>(null);
	const { isTruncated, isReadingMore, setIsReadingMore } = useTruncatedElement(descriptionRef);

	const isOverdueFlag = task.dueAt ? isOverdue(task.dueAt) : false;

	return (
		<div className="rounded-lg border border-gray-200 bg-white transition-shadow hover:shadow-md">
			<div className="p-4">
				<div className="mb-3 flex items-start justify-between gap-3">
					<div className="min-w-0 flex-1">
						<div className="mb-2 flex items-center gap-2">
							<Badge intent="none" className={getCategoryColor(task)}>
								{getCategoryLabel(task.category)}
							</Badge>
							{/* <span className={`rounded px-2 py-1 text-xs font-medium ${getCategoryColor(task)}`}>
								{getCategoryLabel(task.category)}
							</span> */}
							<Badge intent={getStatusIntent(task, isOverdueFlag)}>{task.workflowStageTaskStatusLabel}</Badge>
						</div>
						<h3 className="mb-1 text-base font-semibold text-gray-900">{task.title}</h3>
					</div>

					<div className="flex items-center gap-1">
						<DialogTrigger>
							<PressableButton>
								<Info className="size-5 cursor-pointer text-gray-500" />
							</PressableButton>

							<Popover showArrow className="max-w-xs p-4">
								<div className="space-y-2">
									<div>
										<p className="mb-1 text-xs font-medium text-gray-500">Workflow</p>
										<p className="text-sm text-gray-900">{getTitle(task.workflowSystemName)}</p>
									</div>
									<div>
										<p className="mb-1 text-xs font-medium text-gray-500">Stage</p>
										<p className="text-sm text-gray-900">{getTitle(task.workflowStageSystemName)}</p>
									</div>
								</div>
							</Popover>
						</DialogTrigger>
						<Button
							appearance="ghost"
							Icon={ChevronRight}
							iconClassName="size-4"
							aria-label="View task"
							isPending={isNavigating}
							onPress={() => {
								setIsNavigating(true);
								navigate(`${task.id}`);
							}}
						/>
					</div>
				</div>

				<p ref={descriptionRef} className={`mb-3 break-words text-sm text-gray-600 ${!isReadingMore && 'line-clamp-3'}`}>
					{task.definitionText}
				</p>
				{isTruncated && !isReadingMore && (
					<button
						onClick={() => setIsReadingMore(true)}
						type="button"
						className="mb-3 flex items-center gap-1 text-xs font-medium text-primary-500 transition-colors hover:text-primary-500/80"
					>
						{isTruncated && isReadingMore ? (
							<>
								<ChevronUp className="size-3" />
								Show less
							</>
						) : (
							<>
								<ChevronDown className="size-3" />
								Show more
							</>
						)}
					</button>
				)}

				<div className="flex items-center justify-between text-xs text-gray-500">
					<div className="flex items-center gap-1.5">
						{task.assignedToId ? <User className="size-3.5" /> : <Users className="size-3.5" />}
						<span>{task.assignedToId ? task.assignedToName : 'Everyone'}</span>
					</div>
					<div>
						{task.workflowStageTaskStatusId === WorkflowStageStatusEnum.Completed && task.resolved ? (
							<span className="font-medium text-green-600">Completed: {formatCompletedDate(task.performedOn!)}</span>
						) : (
							<span className={isOverdueFlag ? 'font-medium text-red-600' : ''}>{formatDate(task.dueAt!, isOverdueFlag)}</span>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}
